import { createSelectorCreator, defaultMemoize } from 'reselect';
import isEqual from 'lodash.isequal';
import moment from 'moment';
import get from 'lodash.get';
import groupBy from 'lodash.groupby';
import mapValues from 'lodash.mapvalues';
import flow from 'lodash.flow';
import union from 'lodash.union';
import set from 'lodash.set';
import orderBy from 'lodash.orderby';
import sumBy from 'lodash.sumby';
import { v4 as uuidV4 } from 'uuid';

// Global Selectors
import { selectHarvestEventsArray as selectEventsArray } from 'services/harvestEvents/selectors';

// Selectors
import { makeSelectQueryStringValue } from '../../../../../../workspaces/react-router-selectors';
import {
    selectGroups,
    selectGroupIds
} from '../../../../../../services/groups/selectors';
import { selectEntities } from '../../../../../../services/entities/selectors';
import { selectPickTypes } from '../../../../../../services/pickTypes/selectors';
import { selectVarieties } from '../../../../../../services/varieties/selectors';
import { selectCurrentEntityGroupIds } from '../../../../../../services/groupEntities/selectors';
import { selectCurrentNumberOfDays } from '../../../../../../services/currentNumberOfDays/selectors';
import { selectHideUnusedGroups } from '../../../../../../services/hideUnusedGroups/selectors';

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

const selectNumberOfDays = createDeepEqualSelector(
    selectCurrentNumberOfDays,
    currentNumberOfDaysState => currentNumberOfDaysState
);

// Select the first day of the timeline
const selectFirstDate = createDeepEqualSelector(
    [makeSelectQueryStringValue('date'), selectCurrentNumberOfDays],
    (dateState, numberOfDaysState) => {
        const momentDate = dateState
            ? moment(dateState, 'YYYY-MM-DD')
            : moment();

        if (numberOfDaysState % 7) {
            return momentDate.format('YYYY-MM-DD');
        }
        return momentDate.startOf('isoWeek').format('YYYY-MM-DD');
    }
);

// Select the last day of the timeline
const selectLastDate = createDeepEqualSelector(
    [selectFirstDate, selectNumberOfDays],
    (firstState, numberOfDaysState) =>
        moment(firstState, 'YYYY-MM-DD')
            .add(numberOfDaysState - 1, 'day')
            .format('YYYY-MM-DD')
);

const selectDatesOfTheWeek = createDeepEqualSelector(
    [selectFirstDate, selectCurrentNumberOfDays],
    (firstDateState, currentNumberOfDaysState) => {
        const momentDate = moment(firstDateState, 'YYYY-MM-DD');
        const datesOfTheWeek = [];

        for (let i = 0; i < currentNumberOfDaysState; i += 1) {
            datesOfTheWeek.push(momentDate.format('YYYY-MM-DD'));
            momentDate.add(1, 'day');
        }
        return datesOfTheWeek;
    }
);

// Select all events with any relationships required for filtering
const selectDenormalizedData = createDeepEqualSelector(
    [
        selectEventsArray,
        selectEntities,
        selectVarieties,
        selectGroups,
        selectPickTypes
    ],
    (
        eventsArrayState,
        entitiesState,
        varietiesState,
        groupsState,
        pickTypesState
    ) =>
        eventsArrayState.map(value => {
            const {
                entityId,
                forecastedQuantity,
                groupIds,
                harvestEvent,
                pickTypeName
            } = value;
            const entity = get(entitiesState, entityId);
            const defaultVarietyId = get(entity, 'defaultVarietyId');
            const variety = get(varietiesState, defaultVarietyId, {});

            const pickTypeId = get(value, ['harvestEvent', 'pickTypeId']);
            const pickType = get(pickTypesState, pickTypeId, {
                name: pickTypeName
            });

            return {
                ...value,
                forecastedQuantity: forecastedQuantity || '',
                // Remove groupId if group doesn't exist
                groupIds: groupIds
                    ? groupIds.filter(groupId => get(groupsState, groupId))
                    : [],
                entity: entity
                    ? {
                          ...entity,
                          variety
                      }
                    : null,
                harvestEvent: {
                    ...harvestEvent,
                    pickType
                }
            };
        })
);

// Select events that match the filters that the user has selected
const selectFilteredData = createDeepEqualSelector(
    [
        selectDenormalizedData,
        makeSelectQueryStringValue('pickTypeName'),
        makeSelectQueryStringValue('varieties'),
        makeSelectQueryStringValue('entities'),
        makeSelectQueryStringValue('groups')
    ],
    (
        denormalizedDataState,
        pickTypeNameState,
        varietiesState,
        entitiesState,
        queryStringGroupsState
    ) =>
        denormalizedDataState.filter(value => {
            const { entity, groupIds, quantitySchedules } = value;
            const varietyId = get(value, 'entity.defaultVarietyId');
            const path = get(value, 'entity.path');

            const pickTypeName = get(value, [
                'harvestEvent',
                'pickType',
                'name'
            ]);

            // Filter out archive entities
            if (!entity || entity.archivedAt || entity.deletedAt) {
                return false;
            }

            if (
                pickTypeNameState &&
                !pickTypeNameState.includes(pickTypeName)
            ) {
                return false;
            }
            if (varietiesState && !varietiesState.includes(`${varietyId}`)) {
                return false;
            }
            if (
                entitiesState &&
                !entitiesState.some(entityId =>
                    `${path}/`.includes(`/${entityId}/`)
                )
            ) {
                return false;
            }
            // Only apply groups filter to scheduled jobs
            if (quantitySchedules && quantitySchedules.length) {
                if (
                    queryStringGroupsState &&
                    !queryStringGroupsState.some(groupId => {
                        // Allows for filtering of unassigned groups
                        if (groupId === 'null') {
                            return !groupIds.length;
                        }

                        return groupIds.includes(Number(groupId));
                    })
                ) {
                    return false;
                }
            }

            return value;
        })
);

// Select all events that have been scheduled
const selectRelevantData = createDeepEqualSelector(
    selectFilteredData,
    filteredDataState =>
        filteredDataState.filter(
            ({ quantitySchedules }) =>
                quantitySchedules && quantitySchedules.length
        )
);

const getEventStartDate = event => {
    const { quantitySchedules } = event;
    if (quantitySchedules) {
        return get(quantitySchedules, [0, 'date']);
    }
    return undefined;
};

const getEventEndDate = event => {
    const { quantitySchedules } = event;
    if (quantitySchedules) {
        return get(quantitySchedules, [quantitySchedules.length - 1, 'date']);
    }
    return undefined;
};

/**
 * Determine Whether Two Date Ranges Overlap
 * For more information, check out {@link https://stackoverflow.com/questions/325933/determine-whether-two-date-ranges-overlap/325964}
 * @param {string} startA - The start date of the first date range, with the format YYYY-MM-DD
 * @param {string} endA - The end date of the first date range, with the format YYYY-MM-DD
 * @param {string} startB - The start date of the second date range, with the format YYYY-MM-DD
 * @param {string} endB - The end date of the second date range, with the format YYYY-MM-DD
 * @return {boolean} - Returns true if the date ranges Overlap
 */
const dateRangesOverlap = (startA, endA, startB, endB) =>
    (startA <= endB && endA >= startB) || (startA <= endB && startB <= endA);

// Select events that exist in the date range currently being viewed by the timeline
const selectPaginatedData = createDeepEqualSelector(
    [selectRelevantData, selectFirstDate, selectLastDate],
    (relevantDataState, firstDateState, lastDateState) =>
        relevantDataState.filter(event =>
            dateRangesOverlap(
                firstDateState,
                lastDateState,
                getEventStartDate(event),
                getEventEndDate(event)
            )
        )
);

const selectHarvestEventsQuantityScheduleTotalsByDate = createDeepEqualSelector(
    selectPaginatedData,
    paginatedDataState => {
        const harvestEventsQuantitySchedules = paginatedDataState.reduce(
            (accumulator, currentValue) => {
                const { quantitySchedules } = currentValue;
                if (quantitySchedules && quantitySchedules.length) {
                    accumulator.push(...quantitySchedules);
                }
                return accumulator;
            },
            []
        );
        return flow(
            value => groupBy(value, 'date'),
            value =>
                mapValues(value, quantitySchedules =>
                    sumBy(quantitySchedules, 'value')
                )
        )(harvestEventsQuantitySchedules);
    }
);

// TODO document what this does
const customGroupBy = (collection, iteratee) => {
    const result = collection.reduce((accumulator, currentValue) => {
        const arrayValues = get(currentValue, iteratee);

        if (!arrayValues || !arrayValues.length) {
            if (accumulator.null) {
                accumulator.null.push(currentValue);
            } else {
                accumulator.null = [currentValue];
            }
        }
        arrayValues.forEach(arrayValue => {
            const key = `${arrayValue}`;
            if (accumulator[key]) {
                accumulator[key].push(currentValue);
            } else {
                accumulator[key] = [currentValue];
            }
        });

        return accumulator;
    }, {});

    return result;
};

const selectHarvestEventsQuantityScheduleTotalsByGroupId = createDeepEqualSelector(
    selectPaginatedData,
    paginatedDataState => {
        return flow(
            value => customGroupBy(value, 'groupIds'),
            value =>
                mapValues(value, harvestEvents => {
                    return sumBy(harvestEvents, harvestEvent => {
                        const quantitySchedules = get(
                            harvestEvent,
                            'quantitySchedules',
                            []
                        );
                        // Default to 1 to allow for calculating unassigned total
                        const numberOfGroups =
                            get(harvestEvent, ['groupIds', 'length']) || 1;

                        return sumBy(quantitySchedules, quantitySchedule => {
                            return quantitySchedule.value / numberOfGroups;
                        });
                    });
                })
        )(paginatedDataState);
    }
);

// Select events orders by startDate and orderedAt so that they appear in the correct order in the timeline
const selectOrderedData = createDeepEqualSelector(
    selectPaginatedData,
    paginatedDataState => {
        return orderBy(paginatedDataState, [
            event => getEventStartDate(event),
            'orderedAt'
        ]);
    }
);

const formatEventsByDate = (events, firstDate) => {
    const dates = {};
    events.forEach(event => {
        const { uuid } = event;

        const startDate = getEventStartDate(event);
        const endDate = getEventEndDate(event);

        // First date of timeline
        const momentFirstDate = moment(firstDate, 'YYYY-MM-DD');

        // Start date of event
        const momentStartDate = moment(startDate, 'YYYY-MM-DD');

        // Find the larger of the two dates
        const firstTimeLineDate = moment.max(momentFirstDate, momentStartDate);

        // End date of event
        const momenEndDate = moment(endDate, 'YYYY-MM-DD');

        // The number of days that the event will be displayed on the timeline
        const numberOfDays = momenEndDate.diff(firstTimeLineDate, 'days');

        // The array of events that are happening on that date, defaults to en empty array if none exist
        const eventsOnDate =
            dates[firstTimeLineDate.format('YYYY-MM-DD')] || [];

        // Find any empty slots for that day
        const firstEmptyIndex = eventsOnDate.findIndex(value => !value);

        // Find the first available slot for that date, if there is an existing empty slot use it, otherwise create a new one.
        const firstAvailableIndex =
            firstEmptyIndex === -1 ? eventsOnDate.length : firstEmptyIndex;

        // Set event uuid at fist available slot
        set(
            dates,
            [firstTimeLineDate.format('YYYY-MM-DD'), firstAvailableIndex],
            uuid
        );

        // If the event spans multiple days set placeholders so that the ui knows to leave space for the event.
        for (let i = 1; i <= numberOfDays; i += 1) {
            const dateToFill = firstTimeLineDate
                .clone()
                .add(i, 'day')
                .format('YYYY-MM-DD');
            set(dates, [dateToFill, firstAvailableIndex], uuidV4());
        }
    });
    return dates;
};

const selectGroupedData = createDeepEqualSelector(
    [selectOrderedData, selectFirstDate],
    (orderedDataState, firstDateState) => {
        return flow(
            value => customGroupBy(value, 'groupIds'),
            value =>
                mapValues(value, events =>
                    formatEventsByDate(events, firstDateState)
                )
        )(orderedDataState);
    }
);

const selectScheduledEventsGroupIds = createDeepEqualSelector(
    selectPaginatedData,
    paginatedDataState => {
        const tackUsedGroups = {};
        return paginatedDataState.reduce((accumulator, currentValue) => {
            const { groupIds } = currentValue;

            if ((!groupIds || !groupIds.length) && !tackUsedGroups.null) {
                tackUsedGroups.null = true;
                accumulator.push(null);
            }

            groupIds.forEach(groupId => {
                if (!tackUsedGroups[groupId]) {
                    tackUsedGroups[groupId] = true;
                    accumulator.push(groupId);
                }
            });
            return accumulator;
        }, []);
    }
);

// Select all ids of groups belonging to the selected top level entity,
// or if no top level entity is selected get all group ids
const selectAllGroupIds = createDeepEqualSelector(
    [selectCurrentEntityGroupIds, selectGroupIds],
    (currentEntityGroupIds, groupIdsState) =>
        currentEntityGroupIds || groupIdsState
);

// Appends null to represent the 'unassigned' group
const selectAllGroupIdsPlusNull = createDeepEqualSelector(
    selectAllGroupIds,
    (allGroupIdsState = []) => allGroupIdsState.concat(null)
);

// Merge top level entity's groups with groups with assigned events or if hideUnusedGroups just return groups with assigned events
const selectMergedGroupIds = createDeepEqualSelector(
    [
        selectHideUnusedGroups,
        selectAllGroupIdsPlusNull,
        selectScheduledEventsGroupIds
    ],
    (
        hideUnusedGroupsState,
        allGroupIdsPlusNullState,
        scheduledEventsGroupIdsState
    ) => {
        if (hideUnusedGroupsState) {
            return scheduledEventsGroupIdsState;
        }
        return union(allGroupIdsPlusNullState, scheduledEventsGroupIdsState);
    }
);

// Filter by groups query string
const selectFilteredMergedGroupIds = createDeepEqualSelector(
    [selectMergedGroupIds, makeSelectQueryStringValue('groups')],
    (mergedGroupIdsState, queryStringGroupsState) =>
        mergedGroupIdsState.filter(value => {
            if (
                queryStringGroupsState &&
                !queryStringGroupsState.includes(`${value}`)
            ) {
                return false;
            }
            return true;
        })
);

// Select groups so they can be sorted
const selectFilteredMergedGroups = createDeepEqualSelector(
    [selectFilteredMergedGroupIds, selectGroups],
    (filteredMergedGroupIdsState, groupsState) =>
        filteredMergedGroupIdsState.map(value => groupsState[value] || value)
);

const selectOrderedFilteredMergedGroups = createDeepEqualSelector(
    selectFilteredMergedGroups,
    filteredMergedGroupsState => orderBy(filteredMergedGroupsState, 'name')
);

const selectEventsTimelineGroupIds = createDeepEqualSelector(
    selectOrderedFilteredMergedGroups,
    orderedFilteredMergedGroupsState =>
        orderedFilteredMergedGroupsState.map(value =>
            value ? value.id : value
        )
);

export {
    selectNumberOfDays,
    selectFirstDate,
    selectLastDate,
    selectDatesOfTheWeek,
    selectFilteredData,
    selectHarvestEventsQuantityScheduleTotalsByDate,
    selectHarvestEventsQuantityScheduleTotalsByGroupId,
    selectGroupedData,
    selectScheduledEventsGroupIds,
    selectAllGroupIds,
    selectAllGroupIdsPlusNull,
    selectEventsTimelineGroupIds
};
